import React from 'react';
import MuiAlert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';

import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useAnnouncements } from 'src/store/AnnouncementsContext';

// Heuristic: treat the string as an i18n key only if it could plausibly
// be one — no spaces, all lowercase letters / digits / dots / underscores
// / hyphens. The legacy banner blanket-wrapped everything in `t()`, which
// is a no-op for free-text admin messages and an unnecessary lookup.
const TRANSLATION_KEY_RE = /^[a-z0-9_.-]+$/;
function maybeTranslate(text, t) {
  if (!text) return '';
  return TRANSLATION_KEY_RE.test(text) ? t(text) : text;
}

const VALID_SEVERITIES = ['info', 'success', 'warning', 'error'];
function normaliseSeverity(s) {
  return VALID_SEVERITIES.includes(s) ? s : 'info';
}

export default function AnnouncementsBanner() {
  const t = useAutoTranslation();
  const { announcements, dismiss } = useAnnouncements();

  if (!announcements?.length) return null;

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {announcements.map((a) => {
        const onClose =
          a.dismissible !== false ? () => dismiss(a._id) : undefined;
        return (
          <MuiAlert
            key={a._id}
            severity={normaliseSeverity(a.severity)}
            onClose={onClose}
            variant="standard"
          >
            {a.title && <AlertTitle>{maybeTranslate(a.title, t)}</AlertTitle>}
            {maybeTranslate(a.body, t)}
            {a.ctaHref && a.ctaLabel && (
              <>
                {' '}
                <Link
                  href={a.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {maybeTranslate(a.ctaLabel, t)}
                </Link>
              </>
            )}
          </MuiAlert>
        );
      })}
    </Stack>
  );
}
